import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  calculateRotationAngle,
  createElement,
  drawElement,
  drawInfiniteGrid,
  drawSelectionUI,
  getResizeHandleAtPosition,
  throttleRAF,
  updateSubAreasPositions,
} from "./drawUtils";
import {
  adjustElementCoordinates,
  adjustmentRequired,
  cursorForPosition,
  getElementAtPosition,
  getMouseCoordinates,
  isColliding,
  resizeElement,
} from "./geometryutils";
import { useCanvasDimensions, usePressedKeys, useWindowReloadWarning, useZoomControl } from "./hooks";
import "./index.css";

import { faArrowLeft, faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useContext } from "react";
import { Store } from "../../store";
import ConfigureShelf from "../ConfigureShelf/ConfigureShelf";

//API
import axios from "../../service/axios";

import LayoutControls from "./LayoutControls/Index";

import LayoutToolBar from "./LayoutToolBar/LayoutToolBar";

import DeleteDialog from "../../shared/components/delete-dialog/DeleteDialog";
import Header from "../Header/Header";

//Icons

//Constants
import { notification } from "antd";
import { debounce, round } from "lodash";
import { toast } from "react-toastify";
import ToolTip from "../../shared/components/ToolTip/ToolTip";
import { safeCopyToClipboard, safePasteFromClipboard } from "../../utils/clipboardUtils";
import { objectTypeMapping } from "../../utils/constant";
import { pixelsToUnits } from "../../utils/helpers";
import ViewModeToggle from "../Switch/ViewModeToggle";
import DirectionIndicator from "../Visualizer/components/DirectionIndicator/DirectionIndicator";
import HoveredElementInfo from "./HoveredElementInfo/HoveredElementInfo";
import { findNearestSnapPoint, getSnapGuides } from "./snappingUtils";

const LayoutEditor = ({ fetchLayoutObjects }) => {
  const { state, dispatch } = useContext(Store);

  const { elements, initialLoad } = state;
  const location = useLocation();
  const canvasRef = useRef(null);
  const canvasParentRef = useRef(null);
  const pressedKeys = usePressedKeys();
  const layoutToolBarRef = useRef(null);

  //Direction detection
  const SouthToNorth = { rackViewing: ["SOUTH", "NORTH"], label: "SN" };
  const EastToWest = { rackViewing: ["EAST", "WEST"], label: "EW" };

  //Shelf state
  const [showShelf, setShowShelf] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPosition, setColorPickerPosition] = useState(0);
  const [deleteElement, setDeleteElement] = useState({});
  const hasDeleteElement = Object.values(deleteElement)?.length > 0;
  const [loading, setLoading] = useState(false);
  const [saveAllLoader, setSaveAllLoader] = useState(false);
  const [clipboard, setClipboard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [layoutName, setlayoutName] = useState(() => localStorage.getItem("layoutName") || "");
  const timeOutRef = useRef(null);
  const isInsideRef = useRef(false);
  const hoveredElement = useRef(null);
  const [elementPosition, setElementPosition] = useState({ x: 0, y: 0 });
  const [snapGuides, setSnapGuides] = useState([]);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStartAngle, setRotationStartAngle] = useState(0);
  const [collisionPreview, setCollisionPreview] = useState(null);
  const snapGuidesRef = useRef(null);
  const collisionPreviewRef = useRef(null);

  const dragRef = useRef({
    action: null, // moving | resizing | rotating | drawing | panning
    elementId: null,
    startMouse: null,
    startElement: null,
    currentElement: null,
    handle: null, // resize / rotate handle
    hasMoved: false,
  });

  const hasElementChanges = elements.some((element) => element.hasElementChanges);

  useWindowReloadWarning(hasElementChanges);

  const { tool, action, canvasDimensions, selectedElement, panOffset, zoom, zoomCenter, startPanMousePosition } = state.layoutEditor;

  // prevents the zoom;
  useZoomControl(zoom, zoomCenter);

  // Update cursor when rotating
  useEffect(() => {
    if (isRotating && canvasRef.current) {
      canvasRef.current.style.cursor = "grabbing";
    }
  }, [isRotating]);
  // resizes the canvas based on the parent element
  useCanvasDimensions(canvasParentRef, canvasRef, (dimensions) => {
    dispatch({
      type: "SET_CANVAS_DIMENSIONS",
      payload: dimensions,
    });
  });

  //ShelfToggler
  const handleShelfToggeler = () => {
    setShowShelf(!showShelf);
    dispatch({ type: "RE_SET_ELEMENTS", payload: [] });
    // if (element) {
    //   dispatch({ type: "SET_SELECTED_ELEMENT", payload: element });
    // }
  };

  const getCopyOfElement = (element) => {
    // try to use mouse position to draw new element
    const elementWidth = element.x2 - element.x1;
    const elementHeight = element.y2 - element.y1;

    if (element?.objectType === "rack") {
      element.bins = element.bins.map((bin) => ({
        ...bin,
        id: null,
        labels: bin.labels.map((label) => ({
          ...label,
          binId: null,
          rackId: null,
        })),
      }));
    }

    if (element?.objectType === "area" && element.subAreas?.length) {
      element.subAreas = element.subAreas.map((subArea) => ({
        ...subArea,
        id: null,
      }));
    }

    return {
      ...element,
      name: `${element.name}_copy`,
      code: `${element.code}_copy`,
      x1: mousePosition.x,
      y1: mousePosition.y,
      x2: mousePosition.x + elementWidth,
      y2: mousePosition.y + elementHeight,
      id: `temp_${Math.floor(Math.random() * 100000)}`,
    };
  };

  /**use refs to hold pan offset and zoom */
  const panOffsetRef = useRef(panOffset);
  const zoomRef = useRef(zoom);
  panOffsetRef.current = panOffset;
  zoomRef.current = zoom;

  const handleWheel = (e) => {
    e.preventDefault();

    const isTrackpad = e.deltaMode === 0;

    const currentZoom = zoomRef.current;
    const currentPan = panOffsetRef.current;

    const rect = canvasRef.current.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    //
    // ZOOM (Ctrl + Wheel)
    //
    if (e.ctrlKey) {
      const zoomFactor = isTrackpad ? 0.004 : 0.0006;
      const zoomDelta = -e.deltaY * zoomFactor;

      const newZoom = Math.min(5, Math.max(0.0025, currentZoom * (1 + zoomDelta)));

      const worldX = (cursorX - currentPan.x) / currentZoom;
      const worldY = (cursorY - currentPan.y) / currentZoom;

      const newPan = {
        x: cursorX - worldX * newZoom,
        y: cursorY - worldY * newZoom,
      };

      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "SET_ZOOM", payload: newZoom },
          { type: "SET_PAN_OFFSET", payload: newPan },
        ],
      });

      return;
    }

    //
    // PAN (Two-finger swipe)
    //
    const panSpeed = isTrackpad ? 1 : 0.4;

    dispatch({
      type: "SET_PAN_OFFSET",
      payload: {
        x: currentPan.x - e.deltaX * panSpeed,
        y: currentPan.y - e.deltaY * panSpeed,
      },
    });
  };

  const smoothWheelHandler = (e) => {
    handleWheel(e);
  };

  // Handle wheel / trackpad input
  const debouncedPanAndZoom = debounce((e) => {
    smoothWheelHandler(e);
  }, 9);

  // handle pan and zoom by track-pad swipe
  const handlePanAndZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();
    debouncedPanAndZoom(e);
  };

  useEffect(() => {
    const canvas = canvasRef.current;

    if (canvas) {
      canvas.addEventListener("wheel", handlePanAndZoom, { passive: false });
      return () => {
        canvas.removeEventListener("wheel", handlePanAndZoom);
      };
    }
  }, []);

  const handleCopy = async (selectedElement) => {
    if (!selectedElement) return;

    await safeCopyToClipboard(selectedElement);

    notification.success({
      message: "Element Copied",
      description: "Element copied to clipboard.",
    });
  };

  const handlePaste = async () => {
    try {
      const element = await safePasteFromClipboard();
      if (element) {
        dispatch({ type: "ADD_ELEMENT", payload: getCopyOfElement(element) });
        notification.success({
          message: "Element Pasted",
          description: "Element pasted from clipboard.",
        });
      } else {
        notification.warning({
          message: "Nothing to Paste",
          description: "No valid element found in clipboard.",
        });
      }
    } catch (err) {
      // Will throw if JSON.parse fails or clipboard is blocked
      notification.warning({
        message: "Invalid Clipboard Data",
        description: "Clipboard does not contain a valid layout element.",
      });
      console.warn("Clipboard content was not a valid JSON element:", err);
    }
  };

  const handleDuplicate = () => {
    if (selectedElement) {
      dispatch({ type: "ADD_ELEMENT", payload: getCopyOfElement(selectedElement) });
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      const ctrlState = e.ctrlKey || e.metaKey;

      // Duplicate element when Ctrl + d is pressed
      if (ctrlState && e.key === "d" && isInsideRef.current) {
        handleDuplicate();
        e.preventDefault();
        return;
      }

      // check if c is pressed and metakey is also pressed
      if (ctrlState && e.key === "c" && isInsideRef.current) {
        handleCopy(selectedElement);
      }

      if (ctrlState && e.key === "v" && isInsideRef.current) {
        handlePaste();
      }

      //check platform that is mac or not
      const isMac = navigator.userAgent.indexOf("Mac") !== -1;

      if ((e.code === "Delete" || (isMac && e.code === "Backspace")) && isInsideRef.current) {
        if (!selectedElement && !selectedElement?.id) return;
        setDeleteElement(selectedElement);
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [clipboard, selectedElement]);

  const updateSelectedShelf = (element) => {
    dispatch({ type: "UPDATE_ELEMENT", payload: element });
  };

  const onRemoveElement = async () => {
    try {
      const { data } = await axios.delete(`layout-objects/${deleteElement.id}`);
      const message = `${deleteElement.objectType} deleted successfully`;
      toast.success(message);
      dispatch({ type: "REMOVE_ELEMENT", payload: deleteElement });
      setDeleteElement({});
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      toast.error(message || defaultErrorMessage, { autoClose: 2000 });
      console.log("error", error);
    }
  };

  const dialogActionHandler = (actionType) => {
    if (actionType === "delete") {
      //Delete unsaved element
      const { id } = deleteElement;
      const isNumber = /^[0-9.]*$/.test(id);
      const isTemp = /^[0-9.]*$/.test(id);
      if (isTemp) {
        onRemoveElement();
      } else {
        dispatch({ type: "REMOVE_ELEMENT", payload: deleteElement });
        setDeleteElement({});
      }
    } else if (actionType === "cancel") {
      setDeleteElement({});
    }
  };

  useEffect(() => {
    // change the cursor to hand when move tool selected
    if (tool === "move") {
      canvasRef.current.style.cursor = "grab";
      if (action === "panning") {
        canvasRef.current.style.cursor = "grabbing";
      }
    } else if (action === "rotating") {
      canvasRef.current.style.cursor = "grabbing";
    } else {
      canvasRef.current.style.cursor = "default";
    }
  }, [tool, action]);

  // whenever elements changes, draw
  const animatingRef = useRef(false);
  useEffect(() => {
    if (animatingRef.current) {
      return;
    }

    animatingRef.current = true;
    const throttledFunc = throttleRAF(renderScene, { trailing: true });
    throttledFunc();

    return () => {
      animatingRef.current = false;
      throttledFunc.flush();
      throttledFunc.cancel();
    };
  }, [elements, selectedElement, action, panOffset, zoom, snapGuides, collisionPreview]);

  const renderScene = () => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawInfiniteGrid({ canvas, ctx, panOffset, zoom });

    elements.forEach((element) => {
      drawElement({
        element,
        ctx,
        action,
        selectedElement,
        zoom,
        panOffset,
      });
    });

    if (selectedElement) {
      const element = elements.find((e) => e.id === selectedElement.id);
      if (element) {
        drawSelectionUI({
          element,
          ctx,
          zoom,
          panOffset,
        });
      }
    }
  };

  function drawSnapGuides({ ctx, guides }) {
    if (snapGuides.length > 0) {
      ctx.save();
      ctx.strokeStyle = "rgba(0, 255, 0, 0.8)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      snapGuides.forEach((guide) => {
        const position = guide.position * zoom + panOffset[guide.type === "vertical" ? "x" : "y"];
        const start = guide.start * zoom + panOffset[guide.type === "vertical" ? "y" : "x"];
        const end = guide.end * zoom + panOffset[guide.type === "vertical" ? "y" : "x"];

        ctx.beginPath();
        if (guide.type === "vertical") {
          ctx.moveTo(position, start);
          ctx.lineTo(position, end);
        } else {
          ctx.moveTo(start, position);
          ctx.lineTo(end, position);
        }
        ctx.stroke();
      });

      ctx.restore();
    }
  }

  function drawCollisionPreview({ ctx, collisionPreview }) {
    if (!collisionPreview) return;
    // Draw collision preview
    if (collisionPreview) {
      ctx.save();
      ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
      ctx.fillStyle = "rgba(255, 0, 0, 0.2)";

      // Highlight the colliding element
      const { element } = collisionPreview;
      const x1 = element.x1 * zoom + panOffset.x;
      const y1 = element.y1 * zoom + panOffset.y;
      const x2 = element.x2 * zoom + panOffset.x;
      const y2 = element.y2 * zoom + panOffset.y;
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;

      if (selectedElement?.objectType === "wall") {
        drawWallCollisionPreview({ ctx, element, zoom, panOffset });
        return;
      }

      ctx.save();
      ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
      ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 8]);

      if (element.rotation && element.rotation !== 0) {
        ctx.translate(centerX, centerY);
        ctx.rotate((element.rotation * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
      }

      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.setLineDash([]);
      ctx.restore();
      // Show collision warning text
      ctx.save();
      ctx.fillStyle = "rgba(255, 0, 0, 0.9)";
      ctx.font = "16px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Collision Detected!", centerX, centerY - 30);
      ctx.restore();
    }
  }

  function drawWallCollisionPreview({ ctx, element, zoom, panOffset }) {
    // Compute wall endpoints in canvas coordinates
    const x1 = element.x1 * zoom + panOffset.x;
    const y1 = element.y1 * zoom + panOffset.y;
    const x2 = element.x2 * zoom + panOffset.x;
    const y2 = element.y2 * zoom + panOffset.y;
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    ctx.save();
    ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
    ctx.lineWidth = 5;
    ctx.setLineDash([10, 8]);

    // Apply rotation if present
    if (element.rotation && element.rotation !== 0) {
      ctx.translate(centerX, centerY);
      ctx.rotate((element.rotation * Math.PI) / 180);
      ctx.translate(-centerX, -centerY);
    }

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Draw collision warning text on top
    ctx.save();
    ctx.fillStyle = "rgba(255, 0, 0, 0.9)";
    ctx.font = "16px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Collision Detected!", centerX, centerY - 30);
    ctx.restore();
  }

  // const handleMouseDown = (event) => {
  //   const { clientX, clientY } = getMouseCoordinates({
  //     event,
  //     zoom,
  //     panOffset,
  //   });
  //   if (tool === "move" || event.button === 1 || pressedKeys.has(" ")) {
  //     dispatch({
  //       type: "BATCH_DISPATCH",
  //       payload: [
  //         { type: "SET_ACTION", payload: "panning" },
  //         {
  //           type: "SET_START_PAN_MOUSE_POSITION",
  //           payload: { x: clientX, y: clientY },
  //         },
  //       ],
  //     });
  //     return;
  //   }

  //   if (tool === "selection") {
  //     // First check if we're clicking on a rotation handle of the currently selected element
  //     if (selectedElement) {
  //       const screenX = clientX * zoom + panOffset.x;
  //       const screenY = clientY * zoom + panOffset.y;
  //       const handle = getResizeHandleAtPosition(screenX, screenY, selectedElement, zoom, panOffset);
  //       if (handle && handle.position === "rotate") {
  //         // Start rotation
  //         const centerX = ((selectedElement.x1 + selectedElement.x2) / 2) * zoom + panOffset.x;
  //         const centerY = ((selectedElement.y1 + selectedElement.y2) / 2) * zoom + panOffset.y;
  //         const initialAngle = calculateRotationAngle(centerX, centerY, screenX, screenY);
  //         setRotationStartAngle(initialAngle - (selectedElement.rotation || 0));
  //         setIsRotating(true);
  //         dispatch({ type: "SET_ACTION", payload: "rotating" });
  //         dispatch({
  //           type: "SET_SELECTED_ELEMENT",
  //           payload: selectedElement, // Keep element selected
  //         });
  //         return; // Exit early to avoid deselection
  //       }
  //       if (handle && (handle.position === "start" || handle.position === "end")) {
  //         dispatch({ type: "SET_ACTION", payload: "resizing" });
  //         dispatch({
  //           type: "SET_SELECTED_ELEMENT",
  //           payload: {
  //             ...selectedElement,
  //             position: handle.position,
  //           }, // Keep element selected
  //         });
  //         return; // Exit early to avoid deselection
  //       }
  //     }

  //     // Then check for element selection
  //     const element = getElementAtPosition(clientX, clientY, elements);
  //     if (element) {
  //       const offsetX = clientX - element.x1;
  //       const offsetY = clientY - element.y1;
  //       dispatch({
  //         type: "SET_SELECTED_ELEMENT",
  //         payload: { ...element, offsetX, offsetY },
  //       });

  //       // TODO
  //       // This will open the element config panel after selecting an element
  //       // The below is commented because, it is opening even when we move the element.
  //       // and the values in config are not updating while we drag/resize/move
  //       // setShowElementConfigPanel(true);

  //       if (element.position === "inside") {
  //         dispatch({ type: "SET_ACTION", payload: "moving" });
  //       } else {
  //         dispatch({ type: "SET_ACTION", payload: "resizing" });
  //       }

  //       layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
  //     } else {
  //       // no element, unselect if any element is selected
  //       if (selectedElement) {
  //         dispatch({
  //           type: "SET_SELECTED_ELEMENT",
  //           payload: null,
  //         });
  //       }
  //     }
  //   } else {
  //     // id, clientX, clientY, clientX, clientY, tool
  //     // if the tool type is rack and there are other racks in the existing area, do not create
  //     const element = createElement({
  //       x1: clientX,
  //       y1: clientY,
  //       x2: clientX,
  //       y2: clientY,
  //       type: tool === "wall" ? "line" : "rectangle",
  //       objectType: tool,
  //       zIndex: elements.length,
  //     });
  //     // if this new element is colliding with any other element in the system, return
  //     // Exclude colliding elements with zones
  //     let collidingElements = elements.filter(
  //       (existingElement) => existingElement.id !== element.id && isColliding(element, existingElement) && existingElement.objectType !== "zone",
  //     );

  //     if (collidingElements.length > 0) {
  //       // Show collision preview but don't prevent creation
  //       setCollisionPreview({ element, collidingElements });
  //       return;
  //     } else {
  //       setCollisionPreview(null);
  //     }

  //     //Element Creation
  //     // batch dispatch
  //     dispatch({
  //       type: "BATCH_DISPATCH",
  //       payload: [
  //         { type: "SET_SELECTED_ELEMENT", payload: element },
  //         { type: "SET_ELEMENTS", payload: [...elements, element] },
  //         { type: "SET_ACTION", payload: "drawing" },
  //       ],
  //     });
  //     layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
  //   }
  // };

  const handleMouseDown = (event) => {
    const { clientX, clientY } = getMouseCoordinates({ event, zoom, panOffset });

    // =========================
    // PAN MODE
    // =========================
    if (tool === "move" || event.button === 1 || pressedKeys.has(" ")) {
      dragRef.current = {
        action: "panning",
        startMouse: { x: clientX, y: clientY },
        startPan: { ...panOffset },
      };
      return;
    }

    // =========================
    // SELECTION TOOL
    // =========================
    if (tool === "selection") {
      if (selectedElement) {
        const screenX = clientX * zoom + panOffset.x;
        const screenY = clientY * zoom + panOffset.y;

        const handle = getResizeHandleAtPosition(screenX, screenY, selectedElement, zoom, panOffset);
        if (handle) {
          // ROTATION
          if (handle.position === "rotate") {
            dragRef.current = {
              action: "rotating",
              elementId: selectedElement.id,
              handle,
              startMouse: { x: clientX, y: clientY },
              startElement: structuredClone(selectedElement),
              currentElement: structuredClone(selectedElement),
              hasMoved: false,
            };
            return;
          }

          // RESIZE (rectangle handles OR wall start/end)
          dragRef.current = {
            action: "resizing",
            elementId: selectedElement.id,
            handle,
            startMouse: { x: clientX, y: clientY },
            startElement: structuredClone(selectedElement),
            currentElement: structuredClone(selectedElement),
            hasMoved: false,
          };
          return;
        }
      }

      // =========================
      // HIT TEST ELEMENT BODY (MOVE)
      // =========================
      const element = getElementAtPosition(clientX, clientY, elements);
      if (element) {
        dispatch({
          type: "SET_SELECTED_ELEMENT",
          payload: element,
        });

        // Since it's not a handle, any hit on the element should default to "moving"
        // for better user experience, especially on rotated objects.
        dragRef.current = {
          action: "moving",
          elementId: element.id,
          startMouse: { x: clientX, y: clientY },
          startElement: structuredClone(element),
          currentElement: structuredClone(element),
          hasMoved: false,
        };

        layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
      } else {
        if (selectedElement) {
          dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
        }
        // Close element config panel if the mouse is clicked outside of an element
        setShowElementConfigPanel(false);
      }
      return;
    }

    // =========================
    // DRAWING MODE
    // =========================
    const element = createElement({
      x1: clientX,
      y1: clientY,
      x2: clientX,
      y2: clientY,
      type: tool === "wall" ? "line" : "rectangle",
      objectType: tool,
      zIndex: elements.length,
    });

    const colliding = elements.filter((el) => el.id !== element.id && el.objectType !== "zone" && isColliding(element, el));

    if (colliding.length > 0) {
      collisionPreviewRef.current = { element, colliding };
      redrawCanvas();
      return;
    }

    dispatch({ type: "SET_ELEMENTS", payload: [...elements, element] });
    dispatch({ type: "SET_SELECTED_ELEMENT", payload: element });

    dragRef.current = {
      action: "drawing",
      elementId: element.id,
      startMouse: { x: clientX, y: clientY },
      startElement: structuredClone(element),
      currentElement: structuredClone(element),
      hasMoved: false,
    };

    layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
  };

  // const handleMouseDown = (event) => {
  //   const { clientX, clientY } = getMouseCoordinates({ event, zoom, panOffset });

  //   // =========================
  //   // PAN MODE (space / middle mouse / move tool)
  //   // =========================
  //   if (tool === "move" || event.button === 1 || pressedKeys.has(" ")) {
  //     dragRef.current = {
  //       action: "panning",
  //       startMouse: { x: clientX, y: clientY },
  //     };
  //     return;
  //   }

  //   // =========================
  //   // SELECTION TOOL
  //   // =========================
  //   if (tool === "selection") {
  //     // ----- 1️⃣ Check handles on selected element -----
  //     if (selectedElement) {
  //       const screenX = clientX * zoom + panOffset.x;
  //       const screenY = clientY * zoom + panOffset.y;

  //       const handle = getResizeHandleAtPosition(screenX, screenY, selectedElement, zoom, panOffset);

  //       if (handle) {
  //         dragRef.current = {
  //           action: handle.position === "rotate" ? "rotating" : "resizing",
  //           elementId: selectedElement.id,
  //           handle,
  //           startMouse: { x: clientX, y: clientY },
  //           startElement: structuredClone(selectedElement),
  //           currentElement: structuredClone(selectedElement),
  //           hasMoved: false,
  //         };
  //         return;
  //       }
  //     }

  //     // ----- 2️⃣ Hit test element -----
  //     const element = getElementAtPosition(clientX, clientY, elements);

  //     if (element) {
  //       // Select element (this is OK in React state)
  //       dispatch({
  //         type: "SET_SELECTED_ELEMENT",
  //         payload: element,
  //       });

  //       // Decide action (move vs resize)
  //       dragRef.current = {
  //         action: element.position === "inside" ? "moving" : "resizing",
  //         elementId: element.id,
  //         startMouse: { x: clientX, y: clientY },
  //         startElement: structuredClone(element),
  //         currentElement: structuredClone(element),
  //         hasMoved: false,
  //       };

  //       layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
  //     } else {
  //       // Clicked empty space → unselect
  //       if (selectedElement) {
  //         dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
  //       }
  //     }

  //     return;
  //   }

  //   // =========================
  //   // DRAWING MODE
  //   // =========================
  //   const element = createElement({
  //     x1: clientX,
  //     y1: clientY,
  //     x2: clientX,
  //     y2: clientY,
  //     type: tool === "wall" ? "line" : "rectangle",
  //     objectType: tool,
  //     zIndex: elements.length,
  //   });

  //   // Collision preview only (no dispatch yet)
  //   const colliding = elements.filter((el) => el.id !== element.id && el.objectType !== "zone" && isColliding(element, el));

  //   if (colliding.length > 0) {
  //     collisionPreviewRef.current = { element, colliding };
  //     redrawCanvas();
  //     return;
  //   }

  //   // Add element ONCE
  //   dispatch({
  //     type: "SET_ELEMENTS",
  //     payload: [...elements, element],
  //   });
  //   dispatch({
  //     type: "SET_SELECTED_ELEMENT",
  //     payload: element,
  //   });

  //   dragRef.current = {
  //     action: "drawing",
  //     elementId: element.id,
  //     startMouse: { x: clientX, y: clientY },
  //     startElement: structuredClone(element),
  //     currentElement: structuredClone(element),
  //     hasMoved: false,
  //   };

  //   layoutToolBarRef.current.onTabChangeHandler(objectTypeMapping[element.objectType]);
  // };

  // const handleMouseUp = (event) => {
  //   const actionsToDispatch = [];

  //   if (tool === "move") {
  //     actionsToDispatch.push({ type: "SET_TOOL", payload: "move" });
  //   } else {
  //     actionsToDispatch.push({ type: "SET_TOOL", payload: "selection" });
  //   }
  //   actionsToDispatch.push({ type: "SET_ACTION", payload: "none" });

  //   // Reset rotation state
  //   if (isRotating) {
  //     setIsRotating(false);
  //     setRotationStartAngle(0);
  //   }

  //   if (selectedElement) {
  //     const index = elements.findIndex((obj) => obj.id === selectedElement.id);
  //     if (index != -1 && (action === "drawing" || action === "resizing") && adjustmentRequired(elements[index].type)) {
  //       const element = elements[index];

  //       if (element.objectType === "wall") {
  //         // Handle "wall" adjustment:
  //         let { x1, y1, x2, y2, width } = element;

  //         const vertical = Math.abs(x2 - x1) < Math.abs(y2 - y1);

  //         if (vertical) {
  //           // Vertical wall: Width is fixed, height can change
  //           // Clamp the width (thickness), only update y1/y2
  //           const centerX = (x1 + x2) / 2;
  //           // You may want to preserve current width (element.width) for calculations
  //           x1 = centerX - width / 2;
  //           x2 = centerX + width / 2;
  //           // y1 and y2 remain as set by user
  //         } else {
  //           // Horizontal wall: Height is fixed, width can change
  //           const centerY = (y1 + y2) / 2;
  //           y1 = centerY - width / 2;
  //           y2 = centerY + width / 2;
  //           // x1 and x2 remain as set by user
  //         }

  //         const wallWidth = round(Math.abs(x2 - x1), 2);
  //         const wallHeight = round(Math.abs(y2 - y1), 2);

  //         // Determine if the wall is "zero size"
  //         if ((vertical && wallHeight === 0) || (!vertical && wallWidth === 0)) {
  //           // actionsToDispatch.push({
  //           //   type: "REMOVE_ELEMENT",
  //           //   payload: element,
  //           // });
  //         } else {
  //           // log element's width

  //           const elementToUpdate = {
  //             ...element,
  //             x1,
  //             y1,
  //             x2,
  //             y2,
  //             positionX: round(x1, 2),
  //             positionY: round(y1, 2),
  //             width: element.width || 5, // preserve thickness!
  //             length: vertical ? wallHeight : wallWidth,
  //             type: element.type,
  //             rotation: element.rotation,
  //             orientation: element.orientation,
  //           };
  //           actionsToDispatch.push({
  //             type: "UPDATE_ELEMENT",
  //             payload: elementToUpdate,
  //           });
  //         }
  //       }
  //       // else {
  //       // // Horizontal wall: Height is fixed, width can change
  //       // const centerY = (y1 + y2) / 2;
  //       // y1 = centerY - width / 2;
  //       // y2 = centerY + width / 2;
  //       // // x1 and x2 remain as set by user
  //       // }

  //       let { x1, y1, x2, y2, width } = element;

  //       const vertical = Math.abs(x2 - x1) < Math.abs(y2 - y1);
  //       const wallWidth = round(Math.abs(x2 - x1), 2);
  //       const wallHeight = round(Math.abs(y2 - y1), 2);

  //       // Determine if the wall is "zero size"
  //       if ((vertical && wallHeight === 0) || (!vertical && wallWidth === 0)) {
  //         // actionsToDispatch.push({
  //         //   type: "REMOVE_ELEMENT",
  //         //   payload: element,
  //         // });
  //       } else {
  //         const elementToUpdate = {
  //           ...element,
  //           x1,
  //           y1,
  //           x2,
  //           y2,
  //           positionX: round(x1, 2),
  //           positionY: round(y1, 2),
  //           width: element.width, // preserve thickness!
  //           length: vertical ? wallHeight : wallWidth,
  //           type: element.type,
  //         };
  //         actionsToDispatch.push({
  //           type: "UPDATE_ELEMENT",
  //           payload: elementToUpdate,
  //         });
  //       }
  //     } else {
  //       // This executes when the element is moving
  //       // Handle "element" adjustment

  //       const { x1, y1, x2, y2 } = adjustElementCoordinates(elements[index]);
  //       const width = round(x2 - x1, 2);
  //       const height = round(y2 - y1, 2);
  //       if (width === 0 || height === 0) {
  //         actionsToDispatch.push({
  //           type: "REMOVE_ELEMENT",
  //           payload: elements[index],
  //         });
  //       } else {
  //         const elementToUpdate = {
  //           ...elements[index],
  //           x1,
  //           y1,
  //           x2,
  //           y2,
  //           positionX: round(x1, 2),
  //           positionY: round(y1, 2),
  //           width: round(x2 - x1, 2),
  //           length: round(y2 - y1, 2),
  //           type: elements[index].type,
  //         };
  //         actionsToDispatch.push({
  //           type: "UPDATE_ELEMENT",
  //           payload: elementToUpdate,
  //         });
  //       }
  //     }
  //     actionsToDispatch.push({
  //       type: "SET_SELECTED_ELEMENT",
  //       payload: elements[index],
  //     });
  //   }
  //   setSnapGuides([]);
  //   // batch dispatch below actions
  //   dispatch({
  //     type: "BATCH_DISPATCH",
  //     payload: actionsToDispatch,
  //   });
  //   // if (action === "drawing") {
  //   //   setShowElementConfigPanel(true);
  //   // }
  // };

  const cleanupAfterCommit = () => {
    dragRef.current = {
      action: "selection",
      elementId: null,
      startElement: null,
      currentElement: null,
      hasMoved: false,
    };

    redrawCanvas();
  };

  const handleMouseUp = () => {
    const drag = dragRef.current;

    // ---- Reset transient UI ----
    snapGuidesRef.current = null;
    collisionPreviewRef.current = null;

    // ---- No active drag → nothing to commit ----
    if (!drag || !drag.hasMoved || !drag.currentElement) {
      dragRef.current = {};
      redrawCanvas();
      return;
    }

    let finalElement = { ...drag.currentElement };

    // ==================================================
    // POST-ADJUSTMENTS (wall / resize / draw corrections)
    // ==================================================

    if ((drag.action === "drawing" || drag.action === "resizing") && adjustmentRequired(finalElement.type)) {
      // if (finalElement.objectType === "wall") {
      //   let { x1, y1, x2, y2, width = 5 } = finalElement;

      //   const vertical = Math.abs(x2 - x1) < Math.abs(y2 - y1);

      //   if (vertical) {
      //     const cx = (x1 + x2) / 2;
      //     x1 = cx - width / 2;
      //     x2 = cx + width / 2;
      //   } else {
      //     const cy = (y1 + y2) / 2;
      //     y1 = cy - width / 2;
      //     y2 = cy + width / 2;
      //   }

      //   const wallWidth = Math.abs(x2 - x1);
      //   const wallHeight = Math.abs(y2 - y1);

      //   // Zero-size wall → remove
      //   if ((vertical && wallHeight === 0) || (!vertical && wallWidth === 0)) {
      //     dispatch({
      //       type: "REMOVE_ELEMENT",
      //       payload: finalElement,
      //     });
      //     cleanupAfterCommit();
      //     return;
      //   }

      //   finalElement = {
      //     ...finalElement,
      //     x1,
      //     y1,
      //     x2,
      //     y2,
      //     positionX: round(x1, 2),
      //     positionY: round(y1, 2),
      //     width,
      //     length: vertical ? round(wallHeight, 2) : round(wallWidth, 2),
      //   };
      // }
      if (finalElement.objectType === "wall") {
        const { x1, y1, x2, y2 } = finalElement;

        const length = Math.hypot(x2 - x1, y2 - y1);

        // Remove zero-length walls
        if (length === 0) {
          dispatch({
            type: "REMOVE_ELEMENT",
            payload: finalElement,
          });
          cleanupAfterCommit();
          return;
        }

        finalElement = {
          ...finalElement,
          x: round(x1, 2),
          y: round(y1, 2),
          positionX: round(x1, 2),
          positionY: round(y1, 2),
          x1: round(x1, 2),
          y1: round(y1, 2),
          x2: round(x2, 2),
          y2: round(y2, 2),
          length: round(length, 2),
          width: finalElement.width || 5, // thickness only
          orientation: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI,
          rotation: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI,
        };
      } else {
        // ---- Non-wall element adjustment ----
        const { x1, y1, x2, y2 } = adjustElementCoordinates(finalElement);
        const w = round(x2 - x1, 2);
        const h = round(y2 - y1, 2);

        if (w === 0 || h === 0) {
          dispatch({
            type: "REMOVE_ELEMENT",
            payload: finalElement,
          });
          cleanupAfterCommit();
          return;
        }

        finalElement = {
          ...finalElement,
          x1,
          y1,
          x2,
          y2,
          x: round(x1, 2),
          y: round(y1, 2),
          positionX: round(x1, 2),
          positionY: round(y1, 2),
          width: w,
          length: h,
        };
      }
    }

    // ==================================================
    // COMMIT ONCE
    // ==================================================
    dispatch({
      type: "UPDATE_ELEMENT",
      payload: finalElement,
    });

    dispatch({
      type: "SET_SELECTED_ELEMENT",
      payload: finalElement,
    });

    // set tool to select
    dispatch({
      type: "SET_TOOL",
      payload: "selection",
    });
    // set action to none
    dispatch({
      type: "SET_ACTION",
      payload: "none",
    });

    cleanupAfterCommit();
  };

  const handleMouseLeave = () => {
    if (isInsideRef.current) {
      isInsideRef.current = false;
    }
  };

  const getRackByPosition = (clientX, clientY, originalX, originalY) => {
    if (!elements || elements.length === 0) return null;

    // Find all elements that contain the point
    let found = null;
    let overlapZone = null;
    for (let i = 0; i < elements.length; ++i) {
      const el = elements[i];
      if (clientX >= el.x1 && clientX <= el.x2 && clientY >= el.y1 && clientY <= el.y2) {
        // Prefer the first non-zone element
        if (el.objectType !== "zone" && !found) found = el;
        // Otherwise store a zone for possible fallback
        else if (el.objectType === "zone" && !found && !overlapZone) overlapZone = el;
      }
    }

    let getElement = found || overlapZone || null;

    // handle timeout and set position only if an element is found
    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current);
      if (elementPosition.x || elementPosition.y) setElementPosition({ x: 0, y: 0 });
    }
    if (getElement) {
      timeOutRef.current = setTimeout(() => {
        const canvas = canvasRef.current;
        const canvasHorizontalEdge = canvas.width - 0.35 * canvas.height;
        const canvasVerticalEdge = canvas.height - 0.15 * canvas.height;
        const _x = originalX >= canvasHorizontalEdge ? canvasHorizontalEdge : originalX + 10;
        const _y = originalY >= canvasVerticalEdge ? originalY - 0.15 * canvas.height : originalY + 20;
        setElementPosition({ x: _x, y: _y });
      }, 800);
    }
    if (!getElement) return null;

    // Prepare dimensions
    const dimUnitMapping = {
      feet: "ft",
      inch: "inch",
    };
    let length, breadth, convertedLength, convertedBreadth;

    // Only for rack, also set totalBins
    if (getElement.objectType === "rack") {
      const rackLength = getElement.x2 - getElement.x1;
      const rackBreadth = getElement.y2 - getElement.y1;
      length = Math.max(rackLength, rackBreadth);
      breadth = Math.min(rackLength, rackBreadth);
      convertedLength = round(pixelsToUnits(length, getElement.unitType), 2) + " " + dimUnitMapping[getElement.unitType];
      convertedBreadth = round(pixelsToUnits(breadth, getElement.unitType), 2) + " " + dimUnitMapping[getElement.unitType];
      return {
        ...getElement,
        totalBins: getElement.bins?.length || 0,
        convertedLength,
        convertedBreadth,
      };
    }

    // Non-rack elements
    length = getElement.x2 - getElement.x1;
    breadth = getElement.y2 - getElement.y1;
    convertedLength = round(pixelsToUnits(length, getElement.unitType), 2) + " " + dimUnitMapping[getElement.unitType];
    convertedBreadth = round(pixelsToUnits(breadth, getElement.unitType), 2) + " " + dimUnitMapping[getElement.unitType];
    return {
      ...getElement,
      convertedLength,
      convertedBreadth,
    };
  };

  //Function to getInfo on MouseOver Canvas Element
  const onMouseOverElement = (event) => {
    let { orinalX, orinalY, clientX, clientY } = getMouseCoordinates({
      event,
      zoom,
      panOffset,
    });

    const _hoveredElement = getRackByPosition(clientX, clientY, orinalX, orinalY);
    if (_hoveredElement) {
      hoveredElement.current = _hoveredElement;
    }
    if (orinalX >= 0 && orinalX <= canvasRef.current?.width && orinalY >= 0 && orinalY <= canvasRef.current?.height) {
      isInsideRef.current = true;
    } else {
      isInsideRef.current = false;
    }
  };

  const checkCollision = (elementToUpdate, elements) => {
    // return elements.some((el) => el.id !== element.id && isCollision(element, el));
    const collidingElements = elements.filter((element) => {
      const isDifferentElement = element.id !== elementToUpdate.id;
      // Skip elements colliding with itself
      if (!isDifferentElement) {
        return false;
      }
      const isElementNotZone = element.objectType !== "zone";
      const isUpdatingZone = elementToUpdate.objectType === "zone";
      if (isDifferentElement && isUpdatingZone && isElementNotZone) {
        return false;
      }
      if (!isElementNotZone && !isUpdatingZone) {
        return false;
      }
      // Check collision if both the elements are zones
      if (!isElementNotZone && isDifferentElement) {
        return isColliding(element, elementToUpdate);
      }
      // Check collision if both the elements are colliding
      return isDifferentElement && isElementNotZone && isColliding(element, elementToUpdate);
    });
    if (collidingElements.length > 0) {
      // Show collision preview but allow movement
      collisionPreviewRef.current = { element: elementToUpdate, collidingElements };
      // setCollisionPreview({ element: elementToUpdate, collidingElements });
      return true;
      // Still allow the movement but show warning
    } else {
      collisionPreviewRef.current = null;
      // setCollisionPreview(null);
      return false;
    }
  };

  const handleMouseMove = (event) => {
    const drag = dragRef.current;
    if (!drag || !drag.action) return;

    const { clientX, clientY } = getMouseCoordinates({ event, zoom, panOffset });
    drag.hasMoved = true;

    // ======================
    // PANNING
    // ======================
    if (drag.action === "panning") {
      const dx = (clientX - drag.startMouse.x) * zoom * 0.5;
      const dy = (clientY - drag.startMouse.y) * zoom * 0.5;

      // panOffsetRef.current = {
      //   x: drag.startPan.x + dx,
      //   y: drag.startPan.y + dy,
      // };
      dispatch({
        type: "SET_PAN_OFFSET",
        payload: { x: panOffset.x + dx, y: panOffset.y + dy },
      });

      redrawCanvas();
      return;
    }

    // ======================
    // DRAWING
    // ======================
    if (drag.action === "drawing") {
      const el = drag.startElement;

      drag.currentElement = {
        ...el,
        x2: clientX,
        y2: clientY,
      };

      redrawCanvas();
      return;
    }

    // ======================
    // MOVING
    // ======================
    if (drag.action === "moving") {
      console.log("Moving", drag);

      const { startMouse, startElement } = drag;

      const dx = clientX - startMouse.x;
      const dy = clientY - startMouse.y;

      drag.currentElement = {
        ...startElement,
        x1: startElement.x1 + dx,
        y1: startElement.y1 + dy,
        x2: startElement.x2 + dx,
        y2: startElement.y2 + dy,
      };

      // Uncomment this, if snapping is required
      // snapping
      // const snapped = findNearestSnapPoint({ x: drag.currentElement.x1, y: drag.currentElement.y1 }, elements, zoom, panOffset, drag.elementId);

      // if (snapped.x != null) {
      //   const w = startElement.x2 - startElement.x1;
      //   drag.currentElement.x1 = snapped.x;
      //   drag.currentElement.x2 = snapped.x + w;
      // }

      // if (snapped.y != null) {
      //   const h = startElement.y2 - startElement.y1;
      //   drag.currentElement.y1 = snapped.y;
      //   drag.currentElement.y2 = snapped.y + h;
      // }

      // Check collision
      const collision = checkCollision(drag.currentElement, elements);
      if (collision) {
        drag.currentElement = drag.startElement;
      }
      redrawCanvas();

      return;
    }

    // ======================
    // RESIZING
    // ======================
    if (drag.action === "resizing") {
      console.log("Resizing", drag);
      drag.currentElement = resizeElement(clientX, clientY, drag.startElement, drag.handle.position);
      // Check collision
      const collision = checkCollision(drag.currentElement, elements);
      if (collision) {
        drag.currentElement = drag.startElement;
      }
      redrawCanvas();
      return;
    }

    // ======================
    // ROTATING
    // ======================
    if (drag.action === "rotating") {
      const el = drag.startElement;
      const cx = (el.x1 + el.x2) / 2;
      const cy = (el.y1 + el.y2) / 2;

      const angle = calculateRotationAngle(cx * zoom + panOffset.x, cy * zoom + panOffset.y, clientX * zoom + panOffset.x, clientY * zoom + panOffset.y);

      drag.currentElement = {
        ...el,
        rotation: angle,
        orientation: angle,
      };

      // Check collision
      const collision = checkCollision(drag.currentElement, elements);
      if (collision) {
        drag.currentElement = drag.startElement;
      }
      redrawCanvas();
      return;
    }
  };

  const redrawCanvas = () => {
    const ctx = canvasRef.current.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawInfiniteGrid({ canvas, ctx, panOffset: panOffsetRef.current, zoom });

    elements.forEach((el) => {
      if (dragRef.current.elementId === el.id && dragRef.current.currentElement) {
        drawElement({
          element: dragRef.current.currentElement,
          ctx,
          zoom,
          panOffset: panOffsetRef.current,
        });
      } else {
        drawElement({ element: el, ctx, zoom, panOffset: panOffsetRef.current });
      }
    });

    if (selectedElement) {
      const element = elements.find((e) => e.id === selectedElement.id);
      if (element) {
        drawSelectionUI({
          element,
          ctx,
          zoom,
          panOffset: panOffsetRef.current,
        });
      }
    }

    // Optional overlays
    drawSnapGuides({ ctx, guides: snapGuidesRef.current });
    drawCollisionPreview({ ctx, collisionPreview: collisionPreviewRef.current });
  };

  // const handleMouseMove = (event) => {
  //   const { clientX, clientY } = getMouseCoordinates({
  //     event,
  //     zoom,
  //     panOffset,
  //   });
  //   onMouseOverElement(event);
  //   setMousePosition({ x: clientX, y: clientY });
  //   if (action === "panning") {
  //     const deltaX = (clientX - startPanMousePosition.x) * zoom * 0.5;
  //     const deltaY = (clientY - startPanMousePosition.y) * zoom * 0.5;
  //     dispatch({
  //       type: "SET_PAN_OFFSET",
  //       payload: { x: panOffset.x + deltaX, y: panOffset.y + deltaY },
  //     });
  //     return;
  //   }

  //   if (tool === "selection") {
  //     // Check rotation handle first if an element is selected
  //     if (selectedElement) {
  //       const screenX = clientX * zoom + panOffset.x;
  //       const screenY = clientY * zoom + panOffset.y;
  //       const handle = getResizeHandleAtPosition(screenX, screenY, selectedElement, zoom, panOffset);
  //       if (handle && handle.position === "rotate") {
  //         event.target.style.cursor = "grab";
  //         return;
  //       }
  //     }

  //     const element = getElementAtPosition(clientX, clientY, elements);
  //     if (element) {
  //       const screenX = clientX * zoom + panOffset.x;
  //       const screenY = clientY * zoom + panOffset.y;
  //       let handle = getResizeHandleAtPosition(screenX, screenY, element, zoom, panOffset);

  //       // Wall-specific: only allow cursor if on endpoint or rotation handle
  //       if (element.objectType === "wall" && handle && !["n", "s", "e", "w", "rotate", "move", "start", "end"].includes(handle.position)) {
  //         // handle = null; // Not a valid handle for wall
  //       }

  //       event.target.style.cursor = handle ? handle.cursor : element.objectType === "wall" ? "default" : cursorForPosition(element.position);
  //     } else {
  //       event.target.style.cursor = "default";
  //     }
  //   }

  //   if (action === "drawing") {
  //     setShowElementConfigPanel(false);
  //     const index = elements.findIndex((obj) => obj.id === selectedElement.id);
  //     const { x1, y1, type } = elements[index];

  //     const rackWidth = clientX - x1;
  //     const rackHeight = clientY - y1;
  //     const rackToView = rackWidth > rackHeight ? SouthToNorth : EastToWest;

  //     const snappedPosition = findNearestSnapPoint({ x: clientX, y: clientY }, elements, zoom, panOffset, index);

  //     const guides = getSnapGuides({ x: clientX, y: clientY }, elements, zoom, panOffset, index);
  //     setSnapGuides(guides);

  //     // x1, y1, clientX, clientY, type
  //     const updatedElement = {
  //       ...elements[index],
  //       x2: Math.round(snappedPosition.x || clientX),
  //       y2: Math.round(snappedPosition.y || clientY),
  //       rackView: rackToView,
  //       width: Math.round((snappedPosition.x || clientX) - x1),
  //       height: Math.round((snappedPosition.y || clientY) - y1),
  //     };
  //     for (const elementToCheck of elements) {
  //       if (elementToCheck.id === updatedElement.id) continue;
  //       // If updatedElement is a zone, do not check collision with non-zones
  //       if (updatedElement.objectType === "zone") {
  //         if (elementToCheck.objectType !== "zone") continue;
  //       } else {
  //         // If updatedElement is NOT a zone, do not check collision with zones
  //         if (elementToCheck.objectType === "zone") continue;
  //       }

  //       if (isColliding(updatedElement, elementToCheck)) {
  //         return; // Early exit on first collision
  //       }
  //     }

  //     const elementsCopy = [...elements];
  //     elementsCopy[index] = updatedElement;

  //     dispatch({ type: "SET_ELEMENTS", payload: elementsCopy });
  //   } else if (action === "moving") {
  //     const { id, x1, x2, y1, y2, type, offsetX, offsetY, zIndex } = selectedElement;
  //     const width = x2 - x1;
  //     const height = y2 - y1;

  //     const snappedPosition = findNearestSnapPoint({ x: clientX - offsetX, y: clientY - offsetY }, elements, zoom, panOffset, id);

  //     const guides = getSnapGuides({ x: clientX - offsetX, y: clientY - offsetY }, elements, zoom, panOffset, id);
  //     setSnapGuides(guides);

  //     const newX1 = clientX - offsetX;
  //     const newY1 = clientY - offsetY;
  //     let elementToUpdate = {
  //       ...selectedElement,
  //       id,
  //       x1: snappedPosition.x || newX1,
  //       y1: snappedPosition.y || newY1,
  //       x2: (snappedPosition.x || newX1) + width,
  //       y2: (snappedPosition.y || newY1) + height,
  //       type,
  //       zIndex,
  //     };

  //     const collidingElements = elements.filter((element) => {
  //       const isDifferentElement = element.id !== elementToUpdate.id;

  //       // Skip elements colliding with itself
  //       if (!isDifferentElement) {
  //         return false;
  //       }
  //       const isElementNotZone = element.objectType !== "zone";
  //       const isUpdatingZone = elementToUpdate.objectType === "zone";

  //       if (isDifferentElement && isUpdatingZone && isElementNotZone) {
  //         return false;
  //       }

  //       if (!isElementNotZone && !isUpdatingZone) {
  //         return false;
  //       }
  //       // Check collision if both the elements are zones
  //       if (!isElementNotZone && isDifferentElement) {
  //         return isColliding(element, elementToUpdate);
  //       }

  //       // Check collision if both the elements are colliding
  //       return isDifferentElement && isElementNotZone && isColliding(element, elementToUpdate);
  //     });

  //     if (collidingElements.length > 0) {
  //       // Show collision preview but allow movement
  //       setCollisionPreview({ element: elementToUpdate, collidingElements });
  //       return;
  //       // Still allow the movement but show warning
  //     } else {
  //       setCollisionPreview(null);
  //     }

  //     if (selectedElement?.objectType === "area" && selectedElement?.subAreas?.length > 0) {
  //       const rows_columns = updateSubAreasPositions(elementToUpdate);

  //       elementToUpdate = rows_columns;
  //     }

  //     dispatch({ type: "UPDATE_ELEMENT", payload: elementToUpdate });
  //   } else if (action === "rotating") {
  //     if (selectedElement && isRotating) {
  //       const centerX = (selectedElement.x1 + selectedElement.x2) / 2;
  //       const centerY = (selectedElement.y1 + selectedElement.y2) / 2;
  //       const currentAngle = calculateRotationAngle(
  //         centerX * zoom + panOffset.x,
  //         centerY * zoom + panOffset.y,
  //         clientX * zoom + panOffset.x,
  //         clientY * zoom + panOffset.y,
  //       );
  //       let newRotation = currentAngle - rotationStartAngle;

  //       // Snap logic: for walls, always snap to nearest 90°, for others, snap to 15° if Shift held
  //       if (selectedElement.objectType === "wall") {
  //         // newRotation = Math.round(newRotation / 90) * 90;
  //         return;
  //       } else if (pressedKeys.has("Shift")) {
  //         newRotation = Math.round(newRotation / 15) * 15;
  //       }

  //       // Normalize rotation to 0-360 degrees
  //       while (newRotation < 0) newRotation += 360;
  //       while (newRotation >= 360) newRotation -= 360;

  //       const elementToUpdate = {
  //         ...selectedElement,
  //         rotation: newRotation,
  //         orientation: newRotation,
  //       };

  //       const collidingElements = elements.filter((element) => {
  //         const isDifferentElement = element.id !== elementToUpdate.id;
  //         const isElementNotZone = element.objectType !== "zone";
  //         const isUpdatingZone = elementToUpdate.objectType === "zone";

  //         if (!isDifferentElement) return false; // Always skip self

  //         // Skip elements colliding with other elements
  //         if (isUpdatingZone && isElementNotZone) {
  //           return false;
  //         }
  //         // Check collision if both the elements are zones
  //         if (element.objectType === "zone" && isDifferentElement && isUpdatingZone) {
  //           return isColliding(element, elementToUpdate);
  //         }
  //         // Check collision if both the elements are colliding (which are not zones)
  //         return isElementNotZone && isColliding(element, elementToUpdate);
  //       });

  //       if (collidingElements.length > 0) {
  //         setCollisionPreview({ element: elementToUpdate, collidingElements });
  //         return;
  //       } else {
  //         setCollisionPreview(null);
  //       }

  //       dispatch({ type: "UPDATE_ELEMENT", payload: elementToUpdate });
  //     }
  //   } else if (action === "resizing") {
  //     const { id, type, position, zIndex, ...coordinates } = selectedElement;

  //     const snappedPosition = findNearestSnapPoint({ x: clientX, y: clientY }, elements, zoom, panOffset, id);

  //     const guides = getSnapGuides({ x: clientX, y: clientY }, elements, zoom, panOffset, id);
  //     setSnapGuides(guides);

  //     const elementToUpdate = resizeElement(snappedPosition.x || clientX, snappedPosition.y || clientY, selectedElement, selectedElement.position);
  //     const collidingElements = elements.filter((element) => {
  //       const isDifferentElement = element.id !== elementToUpdate.id;

  //       // Skip elements colliding with itself
  //       if (!isDifferentElement) {
  //         return false;
  //       }

  //       const isElementNotZone = element.objectType !== "zone";
  //       const isUpdatingZone = elementToUpdate.objectType === "zone";

  //       const isElementOverlappingZone = !isUpdatingZone && !isElementNotZone;
  //       const isZoneOverlappingElement = isUpdatingZone && isElementNotZone;

  //       if (isElementOverlappingZone || isZoneOverlappingElement) {
  //         return false;
  //       }

  //       // Check collision if both the elements are zones
  //       if (!isElementNotZone) {
  //         return isColliding(element, elementToUpdate);
  //       }
  //       // Check collision if both the elements are colliding
  //       return isElementNotZone && isColliding(element, elementToUpdate);
  //     });

  //     if (collidingElements.length > 0) {
  //       // Show collision preview
  //       setCollisionPreview({ element: elementToUpdate, collidingElements });
  //       return;
  //       // Still allow resizing but show warning
  //     } else {
  //       setCollisionPreview(null);
  //     }

  //     dispatch({ type: "UPDATE_ELEMENT", payload: elementToUpdate });
  //   } else {
  //     setCollisionPreview(null);
  //   }
  // };

  const handleZoomAction = (currentZoom, zoomBy) => {
    const newZoom = zoom + zoomBy;

    // only clamp max zoom (no min zoom)
    if (newZoom < 0.0025 || newZoom > 5) return;

    const screenCenter = {
      x: canvasDimensions.width / 2,
      y: canvasDimensions.height / 2,
    };

    const mouseX = (screenCenter.x - panOffset.x) / zoom;
    const mouseY = (screenCenter.y - panOffset.y) / zoom;

    const panXOffset = screenCenter.x - mouseX * newZoom;
    const panYOffset = screenCenter.y - mouseY * newZoom;

    dispatch({ type: "SET_PAN_OFFSET", payload: { x: panXOffset, y: panYOffset } });
    dispatch({ type: "SET_ZOOM", payload: newZoom });
  };

  const resetZoom = () => {
    dispatch({ type: "SET_ZOOM", payload: 1 });
  };

  const onSaveALlElements = async () => {
    const warehouseLayoutId = Number(new URLSearchParams(location.search).get("layoutId"));
    setLoading(true);
    setSaveAllLoader(true);
    //Get the elements that have changes
    const modifiedElements = elements.filter((element) => element.hasElementChanges);

    //transform the elements to save
    const elementsToSave = modifiedElements.map((element) => {
      const { name, code, color, type, facingDirection, x, y, x1, y1, x2, y2, objectType, height, id, hasElementChanges, zIndex, width, unitType } = element;

      if (element.objectType === "rack") {
        const transformedBins =
          element?.bins?.map((bin, index) => {
            return {
              id: isNaN(bin.id) ? null : bin.id,
              name: bin.name,
              code: bin.code,
              width: round(pixelsToUnits(bin.width, element.unitType), 2),
              height: round(pixelsToUnits(bin.height, element.unitType), 2),
              depth: bin.depth || 0,
              positionX: round(pixelsToUnits(bin.positionX, element.unitType), 2),
              positionY: round(pixelsToUnits(bin.positionY, element.unitType), 2),
              maxCapacity: bin.maxCapacity || null,
            };
          }) || [];

        let positionX = round(element.x1, 2);
        positionX = pixelsToUnits(positionX, element.unitType);

        let positionY = round(element.y1, 2);
        positionY = pixelsToUnits(positionY, element.unitType);

        const rackWidth = round(x2 - x1, 2);
        const rackHeight = round(y2 - y1, 2);

        let width = element?.width || rackWidth;
        let length = element?.length || rackHeight;

        length = round(y2 - y1, 2);
        length = pixelsToUnits(length, element.unitType);

        width = round(x2 - x1, 2);
        width = pixelsToUnits(width, element.unitType);

        return {
          id: isNaN(+element.id) ? null : element.id,
          name: element.name,
          code: element.code,
          warehouseLayoutId: warehouseLayoutId,
          positionX,
          positionY,
          length,
          width,
          orientation: element?.rotation || 0,
          color: element?.color,
          // type: element.type,
          facingDirection: element?.rackView?.label || "NE",
          objectType: element.objectType,
          // id: element.id,
          hasElementChanges: element?.hasElementChanges,
          zIndex: element?.zIndex,
          bins: transformedBins,
          unitType: element?.unitType,
          rotation: element?.rotation || 0,
          maxCapacity: element?.maxCapacity || null,
        };
      } else if (objectType === "wall") {
        let positionX = round(x1, 2);
        positionX = pixelsToUnits(positionX, element.unitType);

        let positionY = round(y1, 2);
        positionY = pixelsToUnits(positionY, element.unitType);

        let objectWidth = x2 - x1;
        objectWidth = round(objectWidth, 2);
        objectWidth = pixelsToUnits(objectWidth, element.unitType);

        let objectLength = y2 - y1;
        objectLength = round(objectLength, 2);
        objectLength = pixelsToUnits(objectLength, element.unitType);

        const currentRotation = Math.round(element?.rotation ?? element?.orientation ?? 0);

        let payload = {
          id: isNaN(+element.id) ? null : element.id,
          name,
          code,
          warehouseLayoutId,
          positionX,
          positionY,
          length: objectLength,
          width: objectWidth,
          orientation: currentRotation,
          zIndex,
          objectType,
          unitType,
          color,
          hasElementChanges,
          rotation: currentRotation,
          facingDirection: element?.facingDirection || "NE",
        };

        return payload;
      } else if (objectType === "area" || objectType === "dock" || objectType === "zone") {
        let objectWidth = x2 - x1;
        objectWidth = round(objectWidth, 2);
        objectWidth = pixelsToUnits(objectWidth, element.unitType);

        let objectLength = y2 - y1;
        objectLength = round(objectLength, 2);
        objectLength = pixelsToUnits(objectLength, element.unitType);

        let positionX = round(x1, 2);
        positionX = pixelsToUnits(positionX, element.unitType);

        let positionY = round(y1, 2);
        positionY = pixelsToUnits(positionY, element.unitType);

        const currentRotation = Math.round(element?.rotation ?? element?.orientation ?? 0);

        let payload = {
          id: isNaN(+element.id) ? null : element.id,
          name,
          code,
          warehouseLayoutId,
          positionX,
          positionY,
          length: objectLength,
          width: objectWidth,
          orientation: currentRotation,
          zIndex,
          objectType,
          unitType,
          color,
          hasElementChanges,
          rotation: currentRotation,
          facingDirection: element?.facingDirection || "NE",
        };

        if (element.objectType === "area") {
          //find area
          // const _area = state?.elements?.find((element) => element?.id === shelf?.id);
          const subAreas =
            element?.subAreas?.map((subArea, index) => {
              return {
                areaId: element?.id,
                name: subArea?.name,
                code: subArea?.code,
                x: round(pixelsToUnits(subArea?.positionX, element.unitType), 2),
                y: round(pixelsToUnits(subArea?.positionY, element.unitType), 2),
                width: round(pixelsToUnits(subArea?.width, element.unitType), 2),
                height: round(pixelsToUnits(subArea?.height, element.unitType), 2),
                unitType: element.unitType,
                maxCapacity: subArea?.maxCapacity || null,
              };
            }) || [];
          payload = {
            ...payload,
            maxCapacity: element?.maxCapacity || null,
            subAreas: subAreas,
          };
        }

        return payload;
      }
    });

    try {
      console.log("Bulk saving elements payload:", elementsToSave);
      const { data } = await axios.post("layout-objects/save-all", elementsToSave);
      setSaveAllLoader(false);
      toast.success("Elements Saved Successfully");
      setShowShelf(false);
      setLoading(false);
      fetchLayoutObjects();
    } catch (error) {
      setLoading(false);
      const {
        response: {
          data: { message },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      toast.error(message || defaultErrorMessage);
      console.log("error", error);
    } finally {
      setSaveAllLoader(false);
      setLoading(false);
    }
  };

  const handlePanReset = () => {
    dispatch({
      type: "SET_PAN_OFFSET",
      payload: { x: 0, y: 0 },
    });
  };
  // toogle full screen
  const toggleFullScreen = () => {
    // dispatch({
    //   type: "SET_FULLSCREEN_MODE_ENABLED",
    //   payload: !state.FULLSCREEN_MODE_ENABLED,
    // });
    const containerElement = document.querySelector(".container");
    if (state.FULLSCREEN_MODE_ENABLED) {
      document.exitFullscreen();
    } else {
      containerElement.requestFullscreen();
    }
  };

  const [showElementConfigPanel, setShowElementConfigPanel] = useState(false);
  return (
    <>
      <Header backHandler={<FontAwesomeIcon icon={faArrowLeft} className="back-arrow" />} title={layoutName + " Editor"} actions={<ViewModeToggle />} />
      <div className="layout-editor-container-wrapper">
        <div className="layout-editor" id="layout-fullscreen">
          <div className="layout-editor-canvas-wrapper" ref={canvasParentRef} style={{ position: "relative" }}>
            <HoveredElementInfo isVisible={isInsideRef.current} position={elementPosition} element={hoveredElement.current} />

            <canvas
              id="canvas"
              ref={canvasRef}
              width={canvasDimensions.width}
              height={canvasDimensions.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            ></canvas>

            <DirectionIndicator />
            <ToolTip title={"Fullscreen"}>
              <div className="fullscreen-action-wrapper">
                <button className="toggleFullScreen" onClick={toggleFullScreen}>
                  <FontAwesomeIcon icon={state.FULLSCREEN_MODE_ENABLED ? faCompress : faExpand} />
                </button>
              </div>
            </ToolTip>
            <div className="zoom-action-wrapper">
              <div className="zoom-action-main">
                <ToolTip title={"Zoom Out"}>
                  <button className="zoom-action-btn" onClick={() => handleZoomAction(zoom, -0.01)} disabled={zoom <= 0.01}>
                    -
                  </button>
                </ToolTip>
                <div className="zoom-action-stat" onClick={resetZoom}>
                  {round(zoom * 100)}%
                </div>
                <ToolTip title={"Zoom In"}>
                  <button className="zoom-action-btn" onClick={() => handleZoomAction(zoom, 0.01)} disabled={zoom >= 5}>
                    +
                  </button>
                </ToolTip>
              </div>
            </div>

            {/* <div className="reset-origin">
              <button className="reset-origin-btn" onClick={handlePanReset}>
                Reset Origin
              </button>
            </div> */}
          </div>
          <LayoutControls onSetTool={(tool) => dispatch({ type: "SET_TOOL", payload: tool })} currentTool={tool} />
        </div>

        <LayoutToolBar
          ref={layoutToolBarRef}
          setShowShelf={setShowShelf}
          setShowColorPicker={setShowColorPicker}
          showColorPicker={showColorPicker}
          setDeleteElement={setDeleteElement}
          setColorPickerPosition={setColorPickerPosition}
          loading={loading}
          saveAll={onSaveALlElements}
          setShowElementConfigPanel={setShowElementConfigPanel}
          showElementConfigPanel={showElementConfigPanel}
        />

        {showShelf && (
          <div
            className="configure-shelf-container"
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#29292ac2",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              top: "0",
              left: "0",
              zIndex: "10",
            }}
          >
            <div
              className="configure-shelf-wrapper"
              style={{
                width: "95%",
                height: "90%",
              }}
            >
              <ConfigureShelf
                handleShelfToggeler={handleShelfToggeler}
                shelf={selectedElement}
                updateSelectedShelf={updateSelectedShelf}
                fetchLayoutObjects={fetchLayoutObjects}
                setLoading={setLoading}
              />
            </div>
          </div>
        )}
      </div>

      {hasDeleteElement && <DeleteDialog recordToDelete={deleteElement?.name} dialogActionHandler={dialogActionHandler} />}
      {/* {loading && !saveAllLoader && <Loader />} */}
    </>
  );
};

export default LayoutEditor;
